const ACTIVE_STATE_CLASSNAME = 'is-active'

const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

class Carousel extends HTMLElement {
  constructor() {
    super()
    const shadowRoot = this.attachShadow({ mode: 'open' });
		const isArmCream = document.querySelector('body').dataset.templateSuffix === 'landing-pdp-arm-cream' || 'pdp-arm-cream';
		const gap = isArmCream ? '37px' : '24px';
    shadowRoot.innerHTML = `
      <style>
				:host {
					--mobile-slide-width: 100;
				}

				::slotted([slot="content"]) {
					scroll-snap-align: start;
  				flex: 0 0 calc(var(--mobile-slide-width) * 1%);
				}

				.carousel {
					display: grid;
				}

				.carousel__viewport {
					display: flex;
  				overflow-x: auto;
  				scroll-snap-type: x mandatory;

					/** Firefox, IE and Edge */
  				-ms-overflow-style: none
  				scrollbar-width: none
				}

				.carousel__viewport.show-gap + .carousel__nav {
					padding: 0 1.5rem;
				}

				.carousel__viewport::-webkit-scrollbar {
					display: none
				}

				.carousel__nav {
					display: grid;
					gap: 8px;
    			grid-template-columns: auto 1fr auto;
					align-items: center;
					margin: 24px 8px 0 8px;
				}

				${isArmCream && (
					`@media screen and (min-width: 990px) {
						.carousel__nav {
							margin-top: 75px;
						}
					}`
				)}

				.carousel__nav-button {
					display: flex;
					align-items: center;
					justify-content: center;
					padding: 0;
					cursor: pointer;
					background: transparent;
					outline: 0;
					border: 0;
				}

				.carousel__nav-button path {
					stroke: #00629B;
					stroke-width: .5px;
				}

				.carousel__nav-button:disabled {
					opacity: 0.5;
					cursor: not-allowed;
				}

				.carousel__nav-viewport {
					display: flex;
					gap: 1.4rem;
					justify-content: space-evenly;
				}

				.carousel__nav-viewport.is-dots {
					justify-content: center;
				}

				.carousel__nav-indicator {
					display: flex;
					padding: 0;
					cursor: pointer;
					background: transparent;
					outline: 0;
					border: 0;
				}

				.carousel__nav-indicator-image {
					width: 72px;
					height: 72px;
					object-position: center;
					object-fit: cover;
					border-radius: 50%;
					pointer-events: none;
				}

				.carousel__nav-indicator-dot {
					width: .8rem;
					height: .8rem;
					border: 1px solid #00629B;
					border-radius: 50%;
				}

				.carousel__nav-indicator.is-active .carousel__nav-indicator-dot {
					background: #00629B;
				}

				@media (min-width: 1024px) {
					::slotted([slot="content"]) {
						flex: 0 0 100%;
					}
					
					.carousel__nav-viewport {
						gap: 1.6rem;
					}

					.carousel__nav-indicator-dot {
						width: 1.4rem;
						height: 1.4rem;
					}

					.carousel__viewport {
						display: grid;
						grid-template-columns: repeat(3, 1fr);
						gap: ${gap};
						overflow: unset;
					}

					.carousel__viewport.show-gap {
						gap: 67px;

						& .top-reviews__slide {
							padding: 0;
						}
					}

					.carousel__nav {
						display: none;
					}

					.carousel__viewport.is-extended {
						display: flex;
						grid-template-columns: unset;
						overflow-x: auto;
					}

					.carousel__viewport.is-extended + .carousel__nav {
						display: grid;
						max-width: 575px;
						width: 100%;
						justify-self: center;
					}

					.carousel__viewport.is-extended + .carousel__nav .carousel__nav-button svg {
						width: 55px;
						height: 55px;
					}

					.carousel__viewport.is-extended ::slotted([slot="content"]) {
						flex: 0 0 calc(100% / 3 - 24px);
					}
				}
      </style>

      <div class="carousel js-carousel">
				<div class="carousel__viewport js-carousel-viewport">
					<slot name="content"></slot>
				</div>

				<div class="carousel__nav">
					<button
						class="carousel__nav-button js-nav-prev"
						type="button"
						aria-label="Previous slide"
					>
						<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
							<path id="Vector" d="M16 3.25C13.4783 3.25 11.0132 3.99777 8.91648 5.39876C6.81976 6.79975 5.18556 8.79103 4.22054 11.1208C3.25552 13.4505 3.00303 16.0141 3.49499 18.4874C3.98696 20.9607 5.20127 23.2325 6.98439 25.0156C8.76751 26.7987 11.0393 28.0131 13.5126 28.505C15.9859 28.997 18.5495 28.7445 20.8792 27.7795C23.209 26.8144 25.2003 25.1802 26.6012 23.0835C28.0022 20.9868 28.75 18.5217 28.75 16C28.746 12.6197 27.4015 9.379 25.0112 6.98877C22.621 4.59854 19.3803 3.25397 16 3.25ZM16 27.25C13.775 27.25 11.5999 26.5902 9.74984 25.354C7.89979 24.1179 6.45785 22.3609 5.60636 20.3052C4.75488 18.2495 4.53209 15.9875 4.96617 13.8052C5.40025 11.6229 6.47171 9.61839 8.04505 8.04505C9.6184 6.47171 11.623 5.40025 13.8052 4.96617C15.9875 4.53208 18.2495 4.75487 20.3052 5.60636C22.3609 6.45784 24.1179 7.89978 25.354 9.74984C26.5902 11.5999 27.25 13.775 27.25 16C27.2467 18.9827 26.0604 21.8422 23.9513 23.9513C21.8422 26.0604 18.9827 27.2467 16 27.25ZM18.53 11.53L14.0613 16L18.53 20.47C18.6037 20.5387 18.6628 20.6215 18.7038 20.7135C18.7448 20.8055 18.7668 20.9048 18.7686 21.0055C18.7704 21.1062 18.7518 21.2062 18.7141 21.2996C18.6764 21.393 18.6203 21.4778 18.549 21.549C18.4778 21.6203 18.393 21.6764 18.2996 21.7141C18.2062 21.7518 18.1062 21.7704 18.0055 21.7686C17.9048 21.7668 17.8055 21.7448 17.7135 21.7038C17.6215 21.6628 17.5387 21.6037 17.47 21.53L12.47 16.53C12.3296 16.3894 12.2507 16.1988 12.2507 16C12.2507 15.8012 12.3296 15.6106 12.47 15.47L17.47 10.47C17.6122 10.3375 17.8002 10.2654 17.9945 10.2688C18.1888 10.2723 18.3742 10.351 18.5116 10.4884C18.649 10.6258 18.7278 10.8112 18.7312 11.0055C18.7346 11.1998 18.6625 11.3878 18.53 11.53Z" fill="#00629B"/>
						</svg>
					</button>

					<div class="carousel__nav-viewport js-nav-viewport"></div>

					<button
						class="carousel__nav-button js-nav-next"
						type="button"
						aria-label="Next slide"
					>
						<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
							<path id="Vector" d="M16 3.25C13.4783 3.25 11.0132 3.99777 8.91648 5.39876C6.81976 6.79975 5.18556 8.79103 4.22054 11.1208C3.25552 13.4505 3.00303 16.0141 3.49499 18.4874C3.98696 20.9607 5.20127 23.2325 6.98439 25.0156C8.76751 26.7987 11.0393 28.0131 13.5126 28.505C15.9859 28.997 18.5495 28.7445 20.8792 27.7795C23.209 26.8144 25.2003 25.1802 26.6012 23.0835C28.0022 20.9868 28.75 18.5217 28.75 16C28.746 12.6197 27.4015 9.379 25.0112 6.98877C22.621 4.59854 19.3803 3.25397 16 3.25ZM16 27.25C13.775 27.25 11.5999 26.5902 9.74984 25.354C7.89979 24.1179 6.45785 22.3609 5.60636 20.3052C4.75488 18.2495 4.53209 15.9875 4.96617 13.8052C5.40025 11.6229 6.47171 9.61839 8.04505 8.04505C9.6184 6.47171 11.623 5.40025 13.8052 4.96617C15.9875 4.53208 18.2495 4.75487 20.3052 5.60636C22.3609 6.45784 24.1179 7.89978 25.354 9.74984C26.5902 11.5999 27.25 13.775 27.25 16C27.2467 18.9827 26.0604 21.8422 23.9513 23.9513C21.8422 26.0604 18.9827 27.2467 16 27.25ZM19.53 15.47C19.6705 15.6106 19.7493 15.8012 19.7493 16C19.7493 16.1988 19.6705 16.3894 19.53 16.53L14.53 21.53C14.3878 21.6625 14.1998 21.7346 14.0055 21.7312C13.8112 21.7277 13.6258 21.649 13.4884 21.5116C13.351 21.3742 13.2723 21.1888 13.2688 20.9945C13.2654 20.8002 13.3375 20.6122 13.47 20.47L17.9388 16L13.47 11.53C13.3375 11.3878 13.2654 11.1998 13.2688 11.0055C13.2723 10.8112 13.351 10.6258 13.4884 10.4884C13.6258 10.351 13.8112 10.2723 14.0055 10.2688C14.1998 10.2654 14.3878 10.3375 14.53 10.47L19.53 15.47Z" fill="#00629B"/>
						</svg>
					</button>
				</div>
      </div>
    `

		const contentSlotEl = shadowRoot.querySelector('slot[name="content"]')
		
		this.contentSlideEls = contentSlotEl.assignedElements({ flatten: true })
		this.contentViewportEl = shadowRoot.querySelector('.js-carousel-viewport')
		this.navViewportEl = shadowRoot.querySelector('.js-nav-viewport')
		this.navPrevEl = shadowRoot.querySelector('.js-nav-prev')
		this.navNextEl = shadowRoot.querySelector('.js-nav-next')
    
		this.currentSlideIndex = 0
		this.maxSlides = this.contentSlideEls.length
		this.initObserver()
  }

	connectedCallback() {
		const {
			navigationImages,
			mobileSlideWidth
		} = this.dataset

		this.renderNavigation(JSON.parse(navigationImages))

		if (mobileSlideWidth) {

			if (mobileSlideWidth < 100) {
				this.contentViewportEl.classList.add('show-gap')
				this.style.setProperty('--mobile-slide-width', JSON.parse(mobileSlideWidth))
			}
		}

		if (this.maxSlides > 3) {
			this.contentViewportEl.classList.add('is-extended')
			if (this.getVisibleSlideCount() > 1) {
				this.navViewportEl.querySelectorAll('.carousel__nav-indicator').forEach((el, i) => {
					if (i > this.maxSlides - 3) el.style.display = 'none'
				})
			}
		}

		// Add event listeners
		this.attachEvents()

		// Initiate state(s)
		this.updateButtonStates()
		this.showSlides()
		this.updateActiveSlides()
	}

	renderNavigation(navigationImages) {
		const isImages = navigationImages?.length > 0

		// Render navigation items
		this.contentSlideEls.forEach((el, index) => {
			// Create button
			const button = document.createElement('button')
			button.className = `carousel__nav-indicator ${index === this.currentSlideIndex ? "is-active" : ""}`
			button.type = 'button'
			button.addEventListener('click', () => this.navigate(index, true))

			// Create button image
			if (isImages) {
				const image = navigationImages[index]
				if (!image) return

				const img = document.createElement('img')
				img.className = 'carousel__nav-indicator-image'
				img.src = image.src
				img.alt = image.alt
				button.appendChild(img)	
			}
			else {
				const dot = document.createElement('div')
				dot.classList = 'carousel__nav-indicator-dot'
				dot.setAttribute('aria-label', `Navigate to slide ${index + 1}`)
				dot.setAttribute('role', 'button')
				button.appendChild(dot)
			}

			this.navViewportEl.appendChild(button)
		})

		if (!isImages) {
			this.navViewportEl.classList.add('is-dots')
		}
	}

	getVisibleSlideCount() {
  	return window.innerWidth >= 1024 ? 3 : 1
	}

	attachEvents() {
    this.navPrevEl.addEventListener('click', () => this.navigate(-1, false))
    this.navNextEl.addEventListener('click', () => this.navigate(1, false))

		const debouncedScrollHandler = debounce(() => {
			const scrollLeft = this.contentViewportEl.scrollLeft
			const slideWidth = this.contentViewportEl.offsetWidth / this.getVisibleSlideCount()
			const newSlideIndex = Math.round(scrollLeft / slideWidth)
			if (newSlideIndex !== this.currentSlideIndex) {
				this.currentSlideIndex = newSlideIndex
				this.updateNavIndicators()
				this.updateButtonStates()
				this.updateActiveSlides()
			}
		}, 50)

		this.contentViewportEl.addEventListener('scroll', debouncedScrollHandler)

    // window.addEventListener('resize', debounce(() => {
		// 	this.renderNavigation(JSON.parse(this.dataset.navigationImages))
		// 	this.updateNavIndicators()
		// 	this.updateButtonStates()
		// }, 200))
  }

	navigate(target, isDirect) {
		if (isDirect) {
			this.currentSlideIndex = target
		} else {
			if (target === -1 && this.currentSlideIndex > 0) {
				this.currentSlideIndex--
			} else if (target === 1 && this.currentSlideIndex < this.maxSlides - 1) {
				this.currentSlideIndex++
			}
		}

    this.scrollToSlide(this.currentSlideIndex)
		this.updateButtonStates()
		this.updateNavIndicators()
		this.updateActiveSlides()
  }

	updateButtonStates() {
		this.navPrevEl.disabled = this.currentSlideIndex === 0
    this.navNextEl.disabled = this.currentSlideIndex === this.maxSlides - this.getVisibleSlideCount()
	}

	updateNavIndicators() {
		Array.from(this.navViewportEl.children).forEach((el, i) => {
			i === this.currentSlideIndex
				? el.classList.add(ACTIVE_STATE_CLASSNAME)
				: el.classList.remove(ACTIVE_STATE_CLASSNAME)
		})
	}

	updateActiveSlides() {
		this.contentSlideEls.forEach((el, i) => {
			i === this.currentSlideIndex
				? el.classList.add('is-active-slide')
				: el.classList.remove('is-active-slide')
		})
	}

	showSlides() {
		this.contentSlideEls.forEach((el) => el.classList.add(ACTIVE_STATE_CLASSNAME))
	}

	scrollToSlide(slideIndex) {
		const slideWidth = slideIndex * this.contentSlideEls[0].getBoundingClientRect().width
    const newScrollPosition = Math.round(slideWidth)
    this.shadowRoot.querySelector('.js-carousel-viewport').scrollTo({
      left: newScrollPosition,
      behavior: 'smooth'
    })
  }

	initObserver() {
		const observerCallback = (entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					this.contentSlideEls.forEach((el, i) => el.classList.remove('is-active-slide'))
					entry.target.classList.add('is-active-slide')
				}
			})
		}
	
		const observerOptions = {
			root: this.contentViewportEl,
			rootMargin: '0px',
			threshold: [0.5]
		}
	
		this.observer = new IntersectionObserver(observerCallback, observerOptions)
	
		this.contentSlideEls.forEach(slide => {
			this.observer.observe(slide)
		})
	}
}

customElements.define('content-carousel', Carousel)